// 파트너 유입 추적 1단계 — 링크 파라미터 보관·정규화 (순수 함수 + localStorage). 기획 docs/ops/partner-attribution-plan.md §4
//
//   링크:  https://laeebly.io/signup?ref=PT-KIM7&utm_source=instagram&utm_medium=post&utm_campaign=2026-09
//   ① 진입 — captureFromLocation() 이 파라미터를 읽어 **24시간** 보관 (그 안에서는 첫 유입 우선: first 는 안 바꾸고 last 만 덮는다)
//   ② 가입 — Signup 이 readAttribution() 으로 읽어 「추천 코드」 칸을 미리 채우고, **가입할 때 칸에 적힌 코드**를 user.signup_ref 에 넣는다.
//        링크의 UTM·원래 코드는 signup_source 에 통계용으로만 남긴다 (대표 결정 2026-09-11: 정산은 칸의 코드 기준, UTM 은 24시간·통계용)
//   ③ 집계 — 어드민 admin_partner_attribution_summary · 파트너 partner_attribution_mine (마이그레이션 20260909120000)
//
// 쿠키를 쓰지 않는다 — 서버로 자동 전송되지 않고 가입 시 한 번만 읽는다. referrer 는 호스트까지만 남긴다.

export const ATTRIB_STORAGE_KEY = "laeebly_attrib";
export const ATTRIB_TTL_MS = 24 * 60 * 60 * 1000; // 24시간 — 통계용 UTM 보관 창 (2026-09-11, 종전 30일)

/** 담당자 코드(초대 링크 &by=) — 영문·숫자·하이픈·밑줄 1~32자. 형식 밖이면 null */
export function normalizeInviter(v) {
  if (!v) return null;
  const s = String(v).trim();
  return /^[A-Za-z0-9_-]{1,32}$/.test(s) ? s : null;
}

/**
 * 파트너 초대 링크 판정 — /signup?as=partner[&by=담당자코드] (2026-09-11, 기획 partner-center-access-plan §3-2 · 대표 결정 ①)
 * 초대 링크로만 파트너 신청이 열린다. 가입 화면은 띠를 더하고 「추천 코드」 칸을 숨긴다.
 */
export function parsePartnerInvite(search) {
  const p = new URLSearchParams(search || "");
  const asPartner = String(p.get("as") || "").toLowerCase() === "partner";
  return { asPartner, invitedBy: asPartner ? normalizeInviter(p.get("by")) : null };
}

/**
 * 파트너 신청 가입의 insert 값 — buildSignupAttribution 결과에 얹는다.
 * signup_ref 는 비운다(파트너는 유치 귀속 대상이 아니다), signup_intent='partner', signup_source 에 intent·invited_by·entry='partner_invite'.
 * role 은 넣지 않는다 — 어드민 「파트너로 승인」이 준다(user_insert_guard · user_role_company_guard).
 */
export function withPartnerIntent(attrib, { invitedBy = null } = {}) {
  const base = attrib || buildSignupAttribution();
  return {
    signup_ref: null,
    signup_intent: "partner",
    signup_source: { ...(base.signup_source || {}), ref: null, link_ref: null, entry: "partner_invite", intent: "partner", invited_by: normalizeInviter(invitedBy) },
  };
}

export const STANDARD_SOURCES = ["instagram", "youtube", "threads", "blog", "kakao", "x", "tiktok", "naver", "offline", "other"];

/** 파트너 코드 정규화 — 영문·숫자·하이픈 2~16자, 대문자. 형식 밖이면 null */
export function normalizeRef(v) {
  if (!v) return null;
  const s = String(v).trim().toUpperCase();
  return /^[A-Z0-9-]{2,16}$/.test(s) ? s : null;
}

/** utm_source 표준값. 그 외는 'other' (원문은 utm_source_raw 로 보관) */
export function normalizeSource(v) {
  if (!v) return null;
  const s = String(v).trim().toLowerCase();
  return STANDARD_SOURCES.includes(s) ? s : "other";
}

function hostOf(url) {
  try { return url ? new URL(url).host : null; } catch (e) { return null; }
}

function storage() {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch (e) { return null; }
}

/** 현재 주소의 파라미터에서 유입 정보를 뽑는다. 아무것도 없으면 null */
export function parseAttribution(search, { referrer = "", landingPath = "", now = Date.now() } = {}) {
  const p = new URLSearchParams(search || "");
  const ref = normalizeRef(p.get("ref"));
  const rawSource = p.get("utm_source");
  const medium = p.get("utm_medium");
  const campaign = p.get("utm_campaign");
  const content = p.get("utm_content");
  if (!ref && !rawSource && !medium && !campaign) return null;
  return {
    ref,
    utm_source: normalizeSource(rawSource),
    utm_source_raw: rawSource || null,
    utm_medium: medium || null,
    utm_campaign: campaign || null,
    utm_content: content || null,
    landing_path: landingPath || null,
    referrer_host: hostOf(referrer),
    captured_at: new Date(now).toISOString(),
  };
}

/** 진입 시 1회 — 첫 유입 우선. 저장된 first 가 살아 있으면 last 만 갱신한다 */
export function captureFromLocation(loc, referrer = "", now = Date.now()) {
  const st = storage();
  if (!st || !loc) return null;
  const found = parseAttribution(loc.search, { referrer, landingPath: loc.pathname, now });
  if (!found) return readAttribution(now);
  let cur = null;
  try { cur = JSON.parse(st.getItem(ATTRIB_STORAGE_KEY) || "null"); } catch (e) { cur = null; }
  const alive = cur && cur.first && now - Date.parse(cur.first.captured_at) < ATTRIB_TTL_MS;
  const next = alive ? { first: cur.first, last: found } : { first: found, last: null };
  try { st.setItem(ATTRIB_STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* 저장 실패는 무시 */ }
  return next.first;
}

/** 보관된 첫 유입. 24시간 지났으면 null 로 보고 지운다 */
export function readAttribution(now = Date.now()) {
  const st = storage();
  if (!st) return null;
  let cur = null;
  try { cur = JSON.parse(st.getItem(ATTRIB_STORAGE_KEY) || "null"); } catch (e) { cur = null; }
  if (!cur || !cur.first) return null;
  if (now - Date.parse(cur.first.captured_at) >= ATTRIB_TTL_MS) { clearAttribution(); return null; }
  return { ...cur.first, last: cur.last || null };
}

export function clearAttribution() {
  const st = storage();
  if (!st) return;
  try { st.removeItem(ATTRIB_STORAGE_KEY); } catch (e) { /* 무시 */ }
}

/**
 * 가입 insert 에 넣을 값. **가입 때 기록되는 코드가 signup_ref 다** — 정산·유치 집계의 기준.
 *   · locked=true(파트너 링크로 들어와 24시간 안, 대표 결정 2026-09-28 ① · 시안 1-B): 화면에 입력 칸이 없고 **링크 코드로 고정**된다.
 *     manualRef 는 무시한다 — 개발자 도구로 칸을 만들어 넣어도 저장은 링크 코드다.
 *   · locked=false: 칸에 적힌 코드(manualRef). 있는 코드인지는 signupRefCheck 가 먼저 확인한다(대표 결정 ②).
 * 링크의 원래 코드는 link_ref 로, UTM 은 그대로 signup_source 에 통계용으로 보관한다 (2026-09-11).
 * entry: link(링크 코드 그대로) · manual(직접 입력) · organic(코드 없음)
 * refChecked: true(서버가 있는 코드라고 답함) · false(확인 못 함 — fail-open) · null(코드 없음/구버전)
 * @returns { signup_ref: string|null, signup_source: object }
 */
export function buildSignupAttribution({ stored = null, manualRef = "", now = Date.now(), locked = false, refChecked = null } = {}) {
  const linkRef = (stored && stored.ref) || null;
  const manual = normalizeRef(manualRef);
  const ref = locked && linkRef ? linkRef : manual;
  const entry = !ref ? "organic" : (linkRef && ref === linkRef ? "link" : "manual");
  return {
    signup_ref: ref,
    signup_source: {
      ref,
      link_ref: linkRef,
      locked: Boolean(locked && linkRef),
      ref_checked: ref ? refChecked : null,
      utm_source: stored?.utm_source || null,
      utm_source_raw: stored?.utm_source_raw || null,
      utm_medium: stored?.utm_medium || null,
      utm_campaign: stored?.utm_campaign || null,
      utm_content: stored?.utm_content || null,
      landing_path: stored?.landing_path || null,
      referrer_host: stored?.referrer_host || null,
      first_seen_at: stored?.captured_at || null,
      captured_at: new Date(now).toISOString(),
      last: stored?.last || null,
      entry,
    },
  };
}

/**
 * 첫 유입 보관 + 파트너 링크 방문 1건 기록 (2026-09-18, 기획 docs/ops/partner-performance-sources-plan.md §4-3 · 대표 결정 ⑥ — 유입 경로 화면보다 먼저 클릭을 모은다).
 *   captureFromLocation 과 같은 보관 규칙. **첫 유입을 새로 저장한 순간**(24시간 안 재방문·last 갱신은 제외)에만, 그리고 ref(파트너 코드)가 있을 때만
 *   logger({ p_code, p_source, … }) 를 fire-and-forget 으로 부른다 — App.jsx 는 supabase.rpc("partner_link_visit_log", …) 를 넘긴다.
 *   서버(RPC)가 활성 코드·하루 상한·같은 방문자 중복을 거른다. 여기서는 부르기만 하고 결과를 기다리지 않는다(실패해도 화면 무관).
 */
export function captureAndLogVisit(loc, referrer = "", logger = null, now = Date.now()) {
  const before = readAttribution(now);
  const first = captureFromLocation(loc, referrer, now);
  if (!first || !first.ref || typeof logger !== "function") return first;
  const isNew = !before || before.captured_at !== first.captured_at;
  if (!isNew) return first;
  try {
    const r = logger({
      p_code: first.ref, p_source: first.utm_source, p_medium: first.utm_medium, p_campaign: first.utm_campaign,
      p_content: first.utm_content, p_landing: first.landing_path, p_referrer_host: first.referrer_host,
    });
    // 🔒 supabase.rpc() 가 돌려주는 것은 Promise 가 아니라 **then 만 있는 지연 실행 객체**다 — then 을 불러야 요청이 나간다.
    //    (2026-09-21 QA: `.catch` 가 있을 때만 이어 붙였더니 요청이 한 번도 전송되지 않았다.) Promise.resolve 로 감싸 then 을 부르고 실패는 삼킨다.
    Promise.resolve(r).catch(() => { /* 기록 실패는 무시 */ });
  } catch (e) { /* 기록 실패는 무시 */ }
  return first;
}
